import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, TextField, Button, IconButton, Chip, Skeleton } from "@mui/material";
import { useIntl } from "react-intl";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import AddIcon from "@mui/icons-material/Add";
import RestoreIcon from "@mui/icons-material/Restore";

import TitledCard from "../TitledCard";
import { SmartalarmUser } from "./types";
import { uuidv4 } from "../../../../utils/utils";
import { hexInputCheck } from "../utils";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";
import { TemplateItemProps } from "../types";
import { isEqual } from "lodash-es";

interface SmartalarmUserListProps extends TemplateItemProps<SmartalarmUser[]> {}

const SmartalarmUserList = ({ value, saved, onChange, disabled }: SmartalarmUserListProps) => {
  const [indexes, setIndexes] = useState<number[]>([]);

  const { formatMessage } = useIntl();
  const { text: editedText } = useEditedColor();

  const onUserChange = useCallback(
    (index: number, v: SmartalarmUser) => {
      if (!value) {
        return;
      }
      value[index] = v;
      onChange(value);
    },
    [value, onChange]
  );

  const addNewUser = useCallback(() => {
    value.push({ uuid: uuidv4(), name: "", code: "" });
    onChange(value);
    setIndexes(value.map((v, i) => i));
  }, [value, onChange]);

  const removeUser = useCallback(
    (index) => {
      value[index].deleted = true;
      onChange(value);
    },
    [value, onChange]
  );

  const restoreUser = useCallback(
    (index) => {
      if (!value) {
        return;
      }
      delete value[index].deleted;
      onChange(value);
    },
    [value, onChange]
  );

  const restoreUserList = useCallback(() => {
    onChange(saved ? [...saved] : []);
  }, [onChange, saved]);

  useEffect(() => {
    setIndexes(value.map((_, i) => i));
  }, [value]);

  // const overview = useMemo(() => {
  //   if (!value) {
  //     return null;
  //   }
  //   return value.length > 0 && indexes?.length === value.length ? (
  //     <Grid container spacing={0.5} sx={{ padding: 1 }}>
  //       {value.map((user: SmartalarmUser) => (
  //         <Grid item key={`user-${user.uuid}`}>
  //           <HTChip
  //             label={[user.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, user.name].filter((p) => !!p).join(" ")}
  //             code={user.code}
  //           />
  //         </Grid>
  //       ))}
  //     </Grid>
  //   ) : null;
  // }, [indexes, value, formatMessage]);

  const editView = useMemo(() => {
    if (!value) {
      return null;
    }
    return (
      <Grid container spacing={1} style={{ padding: 8 }}>
        {indexes?.length === value.length &&
          indexes.map((index: number) => (
            <Grid item xs={12} key={`user-${value[index].uuid}`}>
              <UserListItem
                value={value[index]}
                saved={saved?.[index] || null}
                onChange={(value: SmartalarmUser) => onUserChange(index, value)}
                onDelete={() => removeUser(index)}
                onRestore={() => restoreUser(index)}
                disabled={disabled}
              />
            </Grid>
          ))}
        <Grid item>
          <Button startIcon={<AddIcon />} onClick={() => addNewUser()} disabled={disabled}>
            {formatMessage({ defaultMessage: "New user" })}
          </Button>
        </Grid>
        {saved && !isEqual(saved, value) ? (
          <Grid item>
            <Button startIcon={<RestoreIcon />} onClick={restoreUserList} sx={{ color: editedText }}>
              {formatMessage({ defaultMessage: "Restore the full user list" })}
            </Button>
          </Grid>
        ) : null}
      </Grid>
    );
  }, [value, indexes, disabled, formatMessage, saved, restoreUserList, editedText, onUserChange, removeUser, restoreUser, addNewUser]);

  if (!value) {
    return (
      <TitledCard title={formatMessage({ defaultMessage: "Users" })}>
        <Grid container spacing={1} sx={{ padding: 1 }}>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
        </Grid>
      </TitledCard>
    );
  }

  return (
    <TitledCard
      title={formatMessage({ defaultMessage: "Users" })}
      // modes={[
      //   { icon: <RemoveRedEyeOutlinedIcon fontSize="small" />, content: overview },
      //   {
      //     icon: disabled ? (
      //       <Badge
      //         badgeContent={<RemoveRedEyeOutlinedIcon fontSize="inherit" />}
      //         anchorOrigin={{
      //           vertical: "bottom",
      //           horizontal: "right",
      //         }}
      //       >
      //         <EditOutlinedIcon fontSize="small" />
      //       </Badge>
      //     ) : (
      //       <EditOutlinedIcon fontSize="small" />
      //     ),
      //     content: editView,
      //   },
      // ]}
    >
      {editView}
    </TitledCard>
  );
};

const UserListItem = ({
  value,
  saved,
  onChange,
  onDelete,
  onRestore,
  disabled,
}: {
  value: SmartalarmUser;
  saved: SmartalarmUser | null;
  onChange: (value: SmartalarmUser) => void;
  onDelete: () => void;
  onRestore: () => void;
  disabled?: boolean;
}) => {
  const [name, setName] = useState(value?.name || "");
  const [code, setCode] = useState(value?.code || "");
  const editedStyle = useEditedStyles();

  const { formatMessage } = useIntl();

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item>
        <TextField
          label={formatMessage({ defaultMessage: "Code" })}
          variant="filled"
          value={code}
          className={saved?.code !== code ? editedStyle.input : ""}
          inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
          onChange={(e) => hexInputCheck(e.target.value, setCode)}
          onBlur={() => {
            const v = code ? code.padStart(2, "0") : code;
            value.code = v;
            setCode(v);
            onChange(value);
          }}
          sx={{ minWidth: 120, width: 120, margin: 0 }}
          size="small"
          autoComplete="off"
          disabled={disabled || value.deleted}
        />
      </Grid>
      <Grid item>
        <TextField
          label={formatMessage({ defaultMessage: "Name" })}
          variant="filled"
          value={name}
          className={saved?.name !== name ? editedStyle.input : ""}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            value.name = name;
            onChange(value);
          }}
          sx={{ margin: 0 }}
          size="small"
          autoComplete="off"
          disabled={disabled || value?.deleted}
        />
      </Grid>
      {!value?.deleted && !disabled && (
        <Grid item>
          <IconButton onClick={() => onDelete()}>
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Grid>
      )}
      {value?.deleted && !disabled && (
        <>
          <Grid item>
            <Chip label={formatMessage({ defaultMessage: "deleted" })} size="small" className={editedStyle.chip} />
          </Grid>
          <Grid item>
            <Button size="small" onClick={onRestore} color="inherit">
              {formatMessage({ defaultMessage: "restore" })}
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default SmartalarmUserList;
